import * as DOMUtils from "history/DOMUtils";
import type { ToolsModalItemType } from "../../../../common-deprecated/features/new-cars/debug-modal/components/ToolsModal";
import { objectToQueryString, queryStringToObject } from "../../../../common-deprecated/utils";
import {
    COMPONENT_CAR_CONFIG,
    COMPONENT_COMPARE_V2,
    COMPONENT_EQUIPMENT_OPTIONAL,
    COMPONENT_EQUIPMENT_STANDARD,
    COMPONENT_PDF,
    ENDPOINT_COMPONENT_MAP,
} from "../../../../shared-logic/server/components";
import { useBnbSelector } from "../redux/store";
import Navigation, { NavStep } from "../../shared-logic/utils/Navigation";
import { isLexus } from "../../../../common-deprecated/settings/utils/commonSettingUtils";

/**
 * Returns a set of build specific debugging links for the tools modal
 */
const useBuildTools = (): ToolsModalItemType[] => {
    const modelId = useBnbSelector((state) => state.navigation.model);
    const carId = useBnbSelector((state) => state.navigation.car);
    const tyCode: string | undefined = useBnbSelector(
        (state) => state.save.data?.Personal?.Code || state.save.data?.ConfigurationCode,
    );
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const step = useBnbSelector((state) => state.navigation.step);

    const { modelMap, country, language, resourcePath } = commonSettings;

    // we take model and car id from navigation because we want this model to work on as many steps as possible
    const carData = useBnbSelector((state) => state.car);
    const colour = useBnbSelector((state) => state.basket?.colour);
    const submodelId = carData?.submodel?.id || "";
    const gradeId = carData?.grade?.id || "";
    const bodyTypeId = carData?.body?.id || "";
    const engineId = carData?.motorization?.engine?.id || "";
    const wheeldriveId = carData?.wheel?.id || "";
    const transmissionId = carData?.motorization?.transmission?.id || "";

    const location = DOMUtils.canUseDOM ? window.location : { origin: "", pathname: "", search: "" };
    const currentPath = `${location.origin}${location.pathname}`;
    const parsedQuery = queryStringToObject(location.search);

    // modeltoken url
    let tokenUrl = "";
    const model = Object.values(modelMap).find((mapModel) => mapModel.id === modelId);
    if (model && model.token) {
        const tokenQuery = objectToQueryString({
            ...parsedQuery,
            modelToken: model.token,
            promoToken: commonSettings.promoToken,
        });
        tokenUrl = `${currentPath}?${tokenQuery}`;
    }

    // other components
    const envEndpoint = `${resourcePath}/${country}/${language}`;
    const carDetailQuery = objectToQueryString({ modelId, carId });
    const standardEquipmentUrl = carId
        ? `${envEndpoint}/${ENDPOINT_COMPONENT_MAP[COMPONENT_EQUIPMENT_STANDARD]}?${carDetailQuery}&carColourId=${
              colour?.id || ""
          }`
        : "";
    const optionalEquipmentUrl = carId
        ? `${envEndpoint}/${ENDPOINT_COMPONENT_MAP[COMPONENT_EQUIPMENT_OPTIONAL]}?${carDetailQuery}&carColourId=${
              colour?.id || ""
          }`
        : "";
    const compareV2Url = gradeId
        ? `${envEndpoint}/${
              ENDPOINT_COMPONENT_MAP[COMPONENT_COMPARE_V2]
          }?compareData=${modelId}:${submodelId}:${gradeId}:${bodyTypeId}:${engineId}:${wheeldriveId}:${transmissionId}:${
              colour?.code || ""
          }`
        : "";
    const pdfUrl = tyCode ? `${envEndpoint}/${ENDPOINT_COMPONENT_MAP[COMPONENT_PDF]}?tyCode=${tyCode}` : "";
    const codDataUrl =
        Navigation.getStepOrderIndex(step, isLexus(commonSettings)) >
        Navigation.getStepOrderIndex(NavStep.Engine, isLexus(commonSettings))
            ? `${resourcePath}/v1/api/cod/data/${country}/${language}${location.search}`
            : "";
    const apheleiaCarconfigUrl = carId
        ? `${envEndpoint}/${ENDPOINT_COMPONENT_MAP[COMPONENT_CAR_CONFIG]}?${objectToQueryString({
              ...parsedQuery,
              path: `configure/${modelId}/${carId}`,
          })}`
        : "";

    return [
        { links: [{ title: "Model token URL", href: tokenUrl, redirect: true }] },
        { links: [{ title: "Set Tradein valuation", onClickLogic: "set-trade-in-valuation" }] },
        {
            title: "Other components:",
            links: [
                standardEquipmentUrl ? { href: standardEquipmentUrl, title: "Standard Equipment" } : false,
                optionalEquipmentUrl ? { href: optionalEquipmentUrl, title: "Optional Equipment" } : false,
                compareV2Url ? { href: compareV2Url, title: "Compare V2" } : false,
                pdfUrl ? { href: pdfUrl, title: "Summary PDF" } : false,
                codDataUrl ? { href: codDataUrl, title: "CoD Data" } : false,
                apheleiaCarconfigUrl ? { href: apheleiaCarconfigUrl, title: "Apheleia carconfig" } : false,
            ],
        },
    ];
};

export default useBuildTools;
